import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useMember } from "@/context/MemberAuthContext";

const NAV = [
  { label: "Products", to: "/products" },
  { label: "Membership", to: "/pricing" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const memberCtx = useMember();
  const isSignedIn = !!(memberCtx && memberCtx.member && memberCtx.member.email);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => location.pathname === to;

  const closeDrawer = () => setOpen(false);

  const handleHomeClick = (e) => {
    closeDrawer();
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full bg-[#F7F7F4] transition-[border-color] duration-200 ${
        scrolled ? "border-b border-[#E7E7E2]" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Wordmark */}
          <Link
            to="/"
            onClick={handleHomeClick}
            data-testid="logo-wordmark"
            className="font-sora text-[22px] md:text-[24px] font-extrabold tracking-tight text-[#111111] leading-none"
          >
            Vaymilo<span className="text-[#2563EB]">.</span>
          </Link>

          {/* Desktop Nav */}
          <nav
            data-testid="nav-header"
            className="hidden md:flex items-center gap-10"
          >
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`nav-link font-manrope text-sm transition-colors ${
                  isActive(n.to)
                    ? "text-[#111111] font-medium"
                    : "text-[#111111]/70 hover:text-[#111111]"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {isSignedIn ? (
              <Link
                to="/account"
                data-testid="btn-header-account"
                className="inline-flex items-center gap-1.5 bg-white text-[#111111] font-manrope font-medium text-sm px-4 py-2.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
              >
                <User size={14} strokeWidth={2.25} />
                Account
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="btn-header-signin"
                className="font-manrope text-sm text-[#111111]/70 hover:text-[#111111] transition-colors"
              >
                Sign in
              </Link>
            )}
            <Link
              to="/pricing"
              data-testid="btn-header-cta"
              className="inline-flex items-center justify-center bg-[#111111] hover:bg-[#2563EB] text-white font-manrope font-medium text-sm px-5 py-2.5 rounded-lg border border-[#111111] hover:border-[#2563EB] transition-colors"
            >
              {isSignedIn ? "Upgrade" : "Get membership"}
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#E7E7E2] bg-white text-[#111111]"
            aria-label="Toggle navigation"
            aria-expanded={open}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-6 pt-2 border-t border-[#E7E7E2]"
          >
            <nav className="flex flex-col">
              {NAV.map((n) => (
                <Link
                  key={n.label}
                  to={n.to}
                  onClick={closeDrawer}
                  data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`py-3 font-manrope text-base border-b border-[#E7E7E2] ${
                    isActive(n.to) ? "text-[#2563EB] font-medium" : "text-[#111111]"
                  }`}
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to={isSignedIn ? "/account" : "/login"}
                onClick={closeDrawer}
                data-testid="mbtn-header-signin"
                className="mt-5 inline-flex items-center justify-center bg-white text-[#111111] border border-[#E7E7E2] font-manrope font-medium text-sm px-5 py-3 rounded-lg"
              >
                {isSignedIn ? "Open my account" : "Sign in"}
              </Link>
              <Link
                to="/pricing"
                onClick={closeDrawer}
                data-testid="mbtn-header-cta"
                className="mt-3 inline-flex items-center justify-center bg-[#111111] text-white font-manrope font-medium text-sm px-5 py-3 rounded-lg"
              >
                {isSignedIn ? "Upgrade membership" : "Get membership"}
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
